import React, { useCallback } from 'react';
import { Box, ClickAwayListener, Tooltip } from '@material-ui/core';
import { Circle } from './index.styled.jsx';
import styled from 'styled-components';
import { MoreHorizRounded } from '@material-ui/icons';
import { useEffectWithErrorLogging } from 'src/utils/useEffectWithErrorLogging';
import {
    logErrorToConsole,
} from 'src/utils/util';

const tooltipClasses = {
    tooltip: 'MuiTooltip-tooltip',
    arrow: 'MuiTooltip-arrow',
    popper: 'MuiTooltip-popper',
};

const HtmlTooltip = styled(
    ({ className, isTemplateScreen, isOrderScreen, ...props }) => (
        // <Tooltip {...props} classes={{ popper: className }} />
        <Tooltip
            {...props}
            classes={{ popper: className }}
            PopperProps={{
                ...props.PopperProps,
                style: { zIndex: 999999 },
            }}
        />
    )
)(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: 'white',
    },

    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: 'white',
        color: 'rgba(0, 0, 0, 0.87)',
        maxWidth: 220,
        fontSize: '12px',
        border: '1px solid #dadde9',
        padding: '10px',
        borderRadius: '2px',
        boxShadow: '0px 0px 6px #00000029',
    },
    [`& .${tooltipClasses.popper}`]: {
        zIndex: 999999,
    },
}));

const customPositionProps = (popperRef, anchorRef, clickPosition) => ({
    popperRef,
    anchorEl: {
        getBoundingClientRect: () => {
            const anchorRect = anchorRef.current?.getBoundingClientRect() || {
                top: 0,
                left: 0,
                width: 0,
                height: 0,
            };
            return new DOMRect(
                clickPosition?.x ?? anchorRect.left,
                anchorRect.top,
                0,
                anchorRect.height
            );
        },
    },
});


const TooltipContent = styled(Box)`
    display: flex;
    align-items: center;
    flex-direction: row;
    cursor: ${({ cursor }) => cursor && cursor};
`;

const MoreIcon = styled(MoreHorizRounded)`
    width: ${({ $width }) => $width || '22px'} !important;
    height: ${({ $height }) => $height || '20px'} !important;
    color: ${({ $color }) => $color || '#1010eb'};
`;

const useDebounce = (callback, delay) => {
    const callbackRef = React.useRef(callback);
    const timeoutRef = React.useRef();

    // Update callback ref when callback changes
    useEffectWithErrorLogging(() => {
        callbackRef.current = callback;
    }, [callback]);

    return useCallback((...args) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
            try {
                callbackRef.current(...args);
            }
            catch (error) {
                logErrorToConsole(error);
            }
        }, delay);
    }, [delay]);
};

const TruncatedTooltip = ({
    text,
    maxLength,
    productCfn,
    isOrderScreen,
    isTemplateScreen,
    isBrand = false,
    iconColor,
    circleWidth,
    circleHeight,
    width,
    maskClassName,
}) => {
    const [isOpen, setIsOpen] = React.useState(false);

    const [clickPosition, setClickPosition] = React.useState(null);
    const [isTruncated, setIsTruncated] = React.useState(false);
    const anchorRef = React.useRef(null);
    const popperRef = React.useRef(null);
    const textRef = React.useRef(null);

    const currentPopperProps = customPositionProps(
        popperRef,
        anchorRef,
        clickPosition
    );
    // : portalDisabledProps;

    const handleClick = (event) => {
        setClickPosition({ x: event.clientX });
        setIsOpen(!isOpen);

        // Update popper position after state changes
        if (popperRef.current) {
            setTimeout(() => {
                try {
                    popperRef.current.update();
                }
                catch (error) {
                    logErrorToConsole(error);
                }
            }, 0);
        }
    };

    const handleClose = () => {
        setIsOpen(false);
        setClickPosition(null);
    };

    // Check for truncation by comparing scrollWidth and clientWidth
    const checkTruncation = () => {
        if (textRef.current) {
            const isOverflowing =
                textRef.current.scrollWidth > textRef.current.clientWidth;
            setIsTruncated(isOverflowing);
        }
    };

    const debouncedCheckTruncation = useDebounce(checkTruncation, 300);

    // useEffectWithErrorLogging to check truncation after rendering
    useEffectWithErrorLogging(() => {
        checkTruncation(); // Initial check (no debounce needed)
        window.addEventListener('resize', debouncedCheckTruncation);

        // Cleanup
        return () => {
            window.removeEventListener('resize', debouncedCheckTruncation);
        };
    }, [text, maxLength, debouncedCheckTruncation]); // Dependency on text and maxLength

    return (
        <ClickAwayListener onClickAway={handleClose}>
            <Box style={{ width: width || (isBrand ? '94%' : '97%') }}>
                <HtmlTooltip
                    open={isOpen}
                    onClose={handleClose}
                    placement="top"
                    anchorEl={anchorRef.current}
                    PopperProps={currentPopperProps}
                    isTemplateScreen={isTemplateScreen}
                    isOrderScreen={isOrderScreen}
                    title={
                        <div>
                            <div
                                style={{
                                    fontWeight: '700',
                                    marginBottom: '3px',
                                }}
                            >
                                {!isBrand ? productCfn : null}
                            </div>
                            <div className={maskClassName}>{text}</div>
                        </div>
                    }
                    arrow
                >
                    <TooltipContent
                        ref={anchorRef}
                        id = 'tooltip-text'
                        onClick={isTruncated && handleClick}
                        cursor={isTruncated && 'pointer'}
                    >
                        <span
                            className={maskClassName}
                            ref={textRef}
                            style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}
                        >
                            {text}
                        </span>
                        {isTruncated && (
                            <Circle $width={circleWidth} $height={circleHeight}>
                                <MoreIcon $color={iconColor} />
                            </Circle>
                        )}
                    </TooltipContent>
                </HtmlTooltip>
            </Box>
        </ClickAwayListener>
    );
};

export default TruncatedTooltip;